import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VisuallyHiddenInput,
  VStack,
} from '@chakra-ui/react';
import { FileUploadBox, Input, Select } from '@gamma/ui/form-fields';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import DownloadSharpIcon from '@gamma/ui/icons/DownloadSharpIcon';
import UploadFileSharpIcon from '@gamma/ui/icons/UploadFileSharpIcon';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { Column } from '@gamma/ui/layout';
import { Alert, Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useContext, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  AlertField,
  EntitySuppressionConfiguration,
} from '@investigator/shared/generated/graphql';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { truncateText } from '@investigator/shared/utils';
import {
  GET_ENTITY_SUPPRESSION_CONFIGURATION,
  useCreateEntitySuppressionConfiguration,
  useDeleteEntitySuppressionConfiguration,
  useGetEntitySuppressionConfiguration,
  useUpdateEntitySuppressionConfiguration,
} from '../../queries';

const { settingPerTenant } = i18n.federatedTenant;

export interface GlobalSuppressionSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTenant?: string;
}

const {
  globalSuppression,
  globalSuppressionSubheader,
  reasonForSuppression,
  reasonForSuppressionPlaceholder,
  importIPAddresses,
  importIPAddressesSubheader,
  fieldsToApplySuppression,
  fieldsToApplySuppressionSubheader,
  sourceIP,
  deletedSuccessMessage,
  genericSuccessMessage,
  IPSuccessMessage,
  suppressIPs,
  save,
  cancel,
  errorMessage,
  uploadedBy,
  uploadedOn,
  deleteFile,
  replaceFile,
  downloadFile,
  xff,
  file,
  deleteFileName,
  deleteWarningText,
  max1000chars,
  atLeastOneField,
  missingReason,
  fileHasNoName,
  fileIsNotTxt,
  noFile,
} = i18n.pages.system.alertCatalog.globalSuppressionModal;

export const suppressionFieldsLabelMap: Record<AlertField, string> = {
  source_ip: sourceIP,
  xff: xff,
};

export type FieldsToSuppressOptions = {
  label: string;
  value: AlertField;
};
export interface GlobalSuppressionFormData {
  reason_for_suppression: string;
  file_name: string;
  file_content: string;
  tenant: string;
  fields_to_suppress: FieldsToSuppressOptions[];
}
export interface FileInfo {
  file_content: string;
  file_name: string;
}
export const fileValidationErrors = ['Filename', 'This file', 'File'];

export const GlobalSuppressionSettingsModal = ({
  isOpen,
  onClose,
  selectedTenant,
}: GlobalSuppressionSettingsModalProps) => {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);
  const showToast = useToast();
  const { isOrgTenant, userLicense } = useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const [rawFile, setRawFile] = useState<File[] | null>();
  const [fileValidationError, setFileValidationError] = useState<string>('');
  const emptyForm = {
    reason_for_suppression: '',
    fields_to_suppress: [],
  };

  const {
    isOpen: deleteWarningModalIsOpen,
    onOpen: deleteWarningModalOnOpen,
    onClose: deleteWarningModalOnClose,
  } = useDisclosure();

  const {
    control,
    watch,
    reset,
    register,
    formState: { isValid, errors },
  } = useForm<GlobalSuppressionFormData>({ mode: 'onChange' });

  const clearLocalFileState = () => {
    setRawFile(null);
    setFileValidationError('');
  };

  const resetFormWithData = (data: Partial<EntitySuppressionConfiguration>) => {
    const { fields_to_suppress, reason_for_suppression } = data;
    let formattedFieldsToSuppress: FieldsToSuppressOptions[] = [];
    if (fields_to_suppress) {
      formattedFieldsToSuppress = fields_to_suppress
        .filter((f): f is AlertField => f !== null) // Backend type is `Maybe<AlertField>[]`. May be an error but we are now handling by filtering this way
        .map((field) => {
          return {
            label: suppressionFieldsLabelMap[field],
            value: field,
          };
        });
    }
    reset({
      reason_for_suppression: reason_for_suppression || '',
      fields_to_suppress: formattedFieldsToSuppress,
    });
  };
  const formData = watch();

  const { loading, data, error } = useGetEntitySuppressionConfiguration({
    variables: {
      tenant: (selectedTenant || tenant) ?? '',
    },
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
    onCompleted: ({ getEntitySuppressionConfiguration: suppressionData }) => {
      if (suppressionData === null || suppressionData?.file_content === null) {
        clearLocalFileState();
        reset(emptyForm);
      } else {
        const { fields_to_suppress, reason_for_suppression } = suppressionData;
        let formattedFieldsToSuppress: FieldsToSuppressOptions[] = [];
        if (fields_to_suppress) {
          formattedFieldsToSuppress = fields_to_suppress
            .filter((f): f is AlertField => f !== null) // Backend type is `Maybe<AlertField>[]`. May be an error but we are now handling by filtering this way
            .map((field) => {
              return {
                label: suppressionFieldsLabelMap[field],
                value: field,
              };
            });
        }
        reset({
          reason_for_suppression: reason_for_suppression || '',
          fields_to_suppress: formattedFieldsToSuppress,
        });
      }
    },
  });

  const suppressionSettingsData = data?.getEntitySuppressionConfiguration;

  const [
    updateEntitySuppressionConfiguration,
    { loading: updateSettingsLoading },
  ] = useUpdateEntitySuppressionConfiguration({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ENTITY_SUPPRESSION_CONFIGURATION,
        variables: { tenant: selectedTenant || tenant },
      },
    ],
    onCompleted: async ({ updateEntitySuppressionConfiguration }) => {
      const successMessage = isOnlySourceIPSelected
        ? IPSuccessMessage
        : genericSuccessMessage;
      showToast({
        description: successMessage,
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
      clearLocalFileState();
      resetFormWithData(updateEntitySuppressionConfiguration);
      onClose();
    },
    onError: (error) => {
      if (
        fileValidationErrors.some((validationError) =>
          error.message.includes(validationError),
        )
      ) {
        setFileValidationError(error.message);
      }
      showToast({
        description: errorMessage,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const [
    createEntitySuppressionConfiguration,
    { loading: createSettingsLoading },
  ] = useCreateEntitySuppressionConfiguration({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ENTITY_SUPPRESSION_CONFIGURATION,
        variables: { tenant: selectedTenant || tenant },
      },
    ],
    onCompleted: ({ createEntitySuppressionConfiguration }) => {
      const successMessage = isOnlySourceIPSelected
        ? IPSuccessMessage
        : genericSuccessMessage;
      showToast({
        description: successMessage,
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
      clearLocalFileState();
      resetFormWithData(createEntitySuppressionConfiguration);
      onClose();
    },
    onError: (error) => {
      if (
        fileValidationErrors.some((validationError) =>
          error.message.includes(validationError),
        )
      ) {
        setFileValidationError(error.message);
      }
      showToast({
        description: errorMessage,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const [
    deleteEntitySuppressionConfiguration,
    { loading: deleteSettingsLoading },
  ] = useDeleteEntitySuppressionConfiguration({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ENTITY_SUPPRESSION_CONFIGURATION,
        variables: { tenant: selectedTenant || tenant },
      },
    ],
    onCompleted: async () => {
      showToast({
        description: deletedSuccessMessage,
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
      handleClose();
    },
    onError: () => {
      showToast({
        description: errorMessage,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const processFile = async (fileList: File[] | undefined) => {
    const file = fileList?.[0];
    if (!file) return null;
    const text = await file.text();
    return { file_name: file.name, file_content: text };
  };

  const handleDownload = () => {
    if (suppressionSettingsData) {
      const content = suppressionSettingsData.file_content;
      const fileName = suppressionSettingsData.file_name ?? 'untitled';
      if (content) {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);

        if (downloadLinkRef.current) {
          downloadLinkRef.current.href = url;
          downloadLinkRef.current.download = fileName;
          downloadLinkRef.current.click();

          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      }
    }
  };

  const handleReplaceClick = () => {
    uploadInputRef.current?.click();
  };

  const isLoading =
    updateSettingsLoading ||
    createSettingsLoading ||
    deleteSettingsLoading ||
    loading;

  const handleClose = () => {
    clearLocalFileState();
    resetFormWithData(suppressionSettingsData || emptyForm);
    onClose();
  };

  const isOnlySourceIPSelected =
    formData.fields_to_suppress?.length === 1 &&
    formData.fields_to_suppress?.[0].value === 'source_ip';

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      title={globalSuppression}
      subtitle={globalSuppressionSubheader}
      body={
        error || isLoading ? (
          <GraphQLReqStatus
            error={error}
            loading={isLoading}
          ></GraphQLReqStatus>
        ) : (
          <VStack alignItems="start">
            {isOrgTenant && (
              <Column>
                <Alert variant="subtle" title={settingPerTenant} />
              </Column>
            )}
            <Input
              {...register('reason_for_suppression', {
                required: missingReason,
                maxLength: { value: 1000, message: max1000chars },
                validate: (value) => {
                  if (typeof value !== 'string') return missingReason;
                  return value?.trim().length > 0 || missingReason;
                },
              })}
              label={reasonForSuppression}
              placeholder={reasonForSuppressionPlaceholder}
              maxLength={1000}
              isRequired
            />
            {errors.reason_for_suppression && (
              <Text color="red.400">
                {errors.reason_for_suppression.message}
              </Text>
            )}
            <Divider marginY={2} />
            {suppressionSettingsData?.file_content ? (
              <>
                <VStack width="100%" alignItems="start">
                  <Text>
                    {importIPAddresses}{' '}
                    <Text as="span" color="red.400">
                      *
                    </Text>
                  </Text>
                  <Text>{importIPAddressesSubheader}</Text>
                </VStack>
                <VStack
                  width="100%"
                  borderRadius={3}
                  border="1px solid"
                  borderColor={fileValidationError ? 'red.400' : 'grey.500'}
                  padding={2}
                  gap={0}
                >
                  <HStack width="100%" justifyContent="space-between">
                    <Text>
                      {rawFile ? (
                        <HStack>
                          {fileValidationError ? (
                            <WarningSharpIcon color="red.400" />
                          ) : (
                            <CheckCircleSharpIcon color="green" />
                          )}
                          <Text>
                            {truncateText(rawFile?.[0]?.name, {
                              shownChars: 10,
                              maxChars: 35,
                            })}
                          </Text>
                        </HStack>
                      ) : (
                        truncateText(suppressionSettingsData?.file_name, {
                          shownChars: 10,
                          maxChars: 35,
                        })
                      )}
                    </Text>
                    <HStack gap={0}>
                      <>
                        <Tooltip label={replaceFile}>
                          <IconButton
                            onClick={handleReplaceClick}
                            data-testid="replace-file-button"
                            aria-label="replace file button"
                            icon={<UploadFileSharpIcon size="sm" />}
                          />
                        </Tooltip>
                        <VisuallyHiddenInput
                          type="file"
                          onChange={(e) => {
                            setFileValidationError('');
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              // This is needed to convert from FileList type to File[]
                              setRawFile(Array.from(files));
                            }
                          }}
                          ref={uploadInputRef}
                        />
                      </>
                      {!rawFile && (
                        <Tooltip label={downloadFile}>
                          <Box>
                            {/* tooltip will not display without this Box */}
                            <a
                              ref={downloadLinkRef}
                              style={{ display: 'none' }}
                            >
                              hidden link to handle download
                            </a>
                            <IconButton
                              onClick={handleDownload}
                              data-testid="download-file-button"
                              aria-label="download file"
                              icon={<DownloadSharpIcon size="sm" />}
                            />
                          </Box>
                        </Tooltip>
                      )}
                      <Tooltip label={deleteFile}>
                        <IconButton
                          data-testid="delete-file-button"
                          aria-label="delete file"
                          icon={
                            rawFile ? (
                              <CloseSharpIcon size="sm" color="red.400" />
                            ) : (
                              <DeleteSharpIcon size="sm" color="red.400" />
                            )
                          }
                          onClick={() => {
                            if (rawFile) {
                              setRawFile(null);
                              setFileValidationError('');
                              if (uploadInputRef?.current) {
                                uploadInputRef.current.value = '';
                              }
                            } else {
                              deleteWarningModalOnOpen();
                            }
                          }}
                        />
                      </Tooltip>
                      <Modal
                        size="xs"
                        onOpen={deleteWarningModalOnOpen}
                        onClose={deleteWarningModalOnClose}
                        isOpen={deleteWarningModalIsOpen}
                        title={i18n.formatString(
                          deleteFileName,
                          truncateText(
                            suppressionSettingsData?.file_name || file,
                            { shownChars: 8, maxChars: 25 },
                          ),
                        )}
                        body={
                          <Alert
                            variant="subtle"
                            status="error"
                            icon={WarningSharpIcon}
                          >
                            <Text>{deleteWarningText}</Text>
                          </Alert>
                        }
                        footer={
                          <HStack>
                            <ButtonGroup justifyContent="flex-end" w="100%">
                              <Button
                                variant="solid"
                                colorScheme="gray"
                                onClick={deleteWarningModalOnClose}
                              >
                                {cancel}
                              </Button>
                              <Button
                                variant="solid"
                                colorScheme="red"
                                isLoading={deleteSettingsLoading}
                                onClick={() => {
                                  deleteEntitySuppressionConfiguration({
                                    variables: {
                                      tenant: (selectedTenant || tenant) ?? '',
                                    },
                                  });
                                  deleteWarningModalOnClose();
                                }}
                              >
                                {deleteFile}
                              </Button>
                            </ButtonGroup>
                          </HStack>
                        }
                      ></Modal>
                    </HStack>
                  </HStack>
                </VStack>
                {fileValidationError && (
                  <Text color="red.400">{fileValidationError}</Text>
                )}
                <HStack width="100%" alignItems="start">
                  {!rawFile && (
                    <Text size="sm">
                      {suppressionSettingsData?.file_updated_by &&
                        i18n.formatString(
                          uploadedBy,
                          suppressionSettingsData.file_updated_by,
                        )}
                      {suppressionSettingsData?.file_updated_at && (
                        <>
                          <Text as="span" color="gray">
                            &nbsp;&bull;&nbsp;
                          </Text>
                          {i18n.formatString(
                            uploadedOn,
                            moment
                              .unix(suppressionSettingsData.file_updated_at)
                              .format('MM/DD/YYYY'),
                          )}
                        </>
                      )}
                    </Text>
                  )}
                </HStack>
              </>
            ) : (
              <FileUploadBox
                name="fileUpload"
                isRequired
                fieldError={fileValidationError}
                label={importIPAddresses}
                helpText={importIPAddressesSubheader}
                onChange={(files) => {
                  setFileValidationError('');
                  setRawFile(files);
                }}
              />
            )}

            <Divider marginY={2} />
            <VStack alignItems="start" borderColor="red">
              <Text>
                {fieldsToApplySuppression}{' '}
                <Text as="span" color="red.400">
                  *
                </Text>
              </Text>
              <Controller
                name="fields_to_suppress"
                control={control}
                rules={{
                  required: atLeastOneField,
                }}
                render={({ field }) => {
                  return (
                    <Select
                      {...field}
                      isMulti
                      width="418px"
                      label={fieldsToApplySuppressionSubheader}
                      name="fields_to_suppress"
                      options={[
                        { label: sourceIP, value: 'source_ip' },
                        { label: xff, value: 'xff' },
                      ]}
                    />
                  );
                }}
              />
              {errors.fields_to_suppress && (
                <Text color="red.400">{errors.fields_to_suppress.message}</Text>
              )}
            </VStack>
          </VStack>
        )
      }
      footer={
        <HStack>
          <ButtonGroup justifyContent="flex-end" w="100%">
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={handleClose}
              isDisabled={false}
            >
              {cancel}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              isDisabled={!isValid || isLoading || Boolean(fileValidationError)}
              isLoading={isLoading}
              onClick={async () => {
                const parsedFieldsToSuppress: AlertField[] =
                  formData.fields_to_suppress?.map(
                    (fieldOption) => fieldOption.value,
                  ) || [];
                const processedFile = rawFile
                  ? await processFile(rawFile)
                  : null;

                if (processedFile) {
                  if (!processedFile.file_name) {
                    setFileValidationError(fileHasNoName);
                    return;
                  }
                  if (!processedFile.file_name.endsWith('.txt')) {
                    setFileValidationError(fileIsNotTxt);
                    return;
                  }
                  if (!processedFile.file_name.replace('.txt', '')) {
                    setFileValidationError(fileHasNoName);
                    return;
                  }
                }

                const configData = {
                  ...formData,
                  fields_to_suppress: parsedFieldsToSuppress,
                };
                if (!processedFile && fileValidationError) {
                  return;
                }
                if (suppressionSettingsData) {
                  updateEntitySuppressionConfiguration({
                    variables: {
                      tenant: (selectedTenant || tenant) ?? '',
                      update_config: {
                        ...configData,
                        ...(processedFile ?? {}),
                        update_file: !!processedFile,
                      },
                    },
                  });
                } else {
                  if (!processedFile) {
                    setFileValidationError(noFile);
                  } else {
                    createEntitySuppressionConfiguration({
                      variables: {
                        tenant: (selectedTenant || tenant) ?? '',
                        create_config: { ...configData, ...processedFile },
                      },
                    });
                  }
                }
              }}
            >
              {isOnlySourceIPSelected ? suppressIPs : save}
            </Button>
          </ButtonGroup>
        </HStack>
      }
    />
  );
};
